import { useState } from "react";
import { Edit3Icon, RotateCcw, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds } from "../../../Components/Utils";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchBannersQuery,
  useFetchTrashedBannersQuery,
  useDeleteBannerMutation,
  useDestroyBannerMutation,
  useRestoreBannerMutation,
  useBulkDeleteBannersMutation,
  useBulkDestroyBannersMutation,
  useBulkRestoreBannersMutation,
} from "../../../stores/api/banners.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { banner_image_url } from "../../../Components/ImageUrls";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "id", label: "#" },
  { key: "image", label: "Image", sortable: false, type: "image", imageBaseUrl: banner_image_url },
  { key: "title", label: "Title" },
  { key: "short_description", label: "Subtitle" },
  { key: "link", label: "Link" },
  { key: "type", label: "Banner Type" },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];

const BannerList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchBannersQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedBannersQuery(queryParams, { skip: !trashed });
  const { data: banners = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteBanner] = useDeleteBannerMutation();
  const [destroyBanner] = useDestroyBannerMutation();
  const [restoreBanner] = useRestoreBannerMutation();
  const [bulkDelete] = useBulkDeleteBannersMutation();
  const [bulkDestroy] = useBulkDestroyBannersMutation();
  const [bulkRestore] = useBulkRestoreBannersMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Banner`,
      message: `${verb} "${row.title}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreBanner(row.id).unwrap();
          else if (type === "destroy") await destroyBanner(row.id).unwrap();
          else await deleteBanner(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `Banner ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = getSelectedIds(selectedItems);
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} banner(s)?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") await bulkRestore(body).unwrap();
          else if (type === "destroy") await bulkDestroy(body).unwrap();
          else await bulkDelete(body).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} banner(s) ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Banners" />
      <TableListing
        headers={HEADERS}
        paginationData={banners}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.CREATE_BANNER}
        handleBulk={handleBulk}
        statusModule="banner"
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <div className="flex gap-2">
            {!trashed ? (
              <Button
                onClick={() => navigate(ROUTE_PATHS.CREATE_BANNER, { state: { banner: row } })}
                title="Edit"
                icon={Edit3Icon}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            ) : (
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            )}
            <Button
              onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />
    </>
  );
};

export default BannerList;
